import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const modalShellClassName = 'flex max-h-[calc(100dvh-1.25rem)] w-full max-w-[29rem] flex-col overflow-hidden rounded-[1rem] border border-slate-300 bg-white ring-1 ring-slate-200/80 shadow-[0_24px_64px_-28px_rgba(15,23,42,0.42)] min-[641px]:max-h-[calc(100dvh-3rem)]';
const modalHeaderClassName = 'shrink-0 px-4 pt-5 min-[641px]:px-6 min-[641px]:pt-6';
const modalBodyClassName = 'min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-5 pt-3 min-[641px]:px-6';
const modalFooterClassName = 'flex shrink-0 flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50/70 px-4 py-3 min-[641px]:flex-row min-[641px]:justify-end min-[641px]:px-6 min-[641px]:py-4';
const modalPrimaryButtonClassName = 'inline-flex min-w-[92px] items-center justify-center rounded-[0.65rem] px-3.5 py-2 text-[13px] font-semibold text-white shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';
const modalSecondaryButtonClassName = 'inline-flex min-w-[84px] items-center justify-center rounded-[0.65rem] border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-semibold text-slate-700 shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';

const copyByAudience = {
  customer: {
    title: 'Archive this order?',
    body: 'The order will be removed from Model Reports list and the cart. Its unpaid invoice is cancelled, the invoice file is removed, and the VIN can be used on a new order.',
    placeholder: 'Enter why this order is being archived',
    checkbox: 'I confirm this order should be archived',
  },
  admin: {
    title: 'Archive this customer order?',
    body: 'This removes the order from the admin list and the customer\'s portal. Its unpaid invoice is cancelled and the invoice file is removed. Restoring later creates a new invoice. The VIN can be used on a new order.',
    placeholder: 'Enter why you are archiving this order',
    checkbox: 'I confirm this customer order should be archived',
  },
};

const ArchiveConfirmModal = ({
  open,
  orderLabel,
  audience = 'customer',
  loading = false,
  onCancel,
  onConfirm,
}) => {
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (open) {
      setReason('');
      setReasonError('');
      setConfirmed(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  const copy = copyByAudience[audience] || copyByAudience.customer;

  const handleConfirm = () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setReasonError('Reason is required');
      return;
    }
    if (!confirmed || loading) return;
    onConfirm(trimmed);
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center overflow-hidden bg-slate-900/28 px-2.5 py-2.5 backdrop-blur-[3px] min-[641px]:px-4 min-[641px]:py-6">
      <div className={modalShellClassName} role="dialog" aria-modal="true" aria-labelledby="archive-order-title">
        <div className={modalHeaderClassName}>
          <div className="inline-flex items-center rounded-[0.55rem] bg-slate-900 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-white">
            Archive order
          </div>
          <div className="mt-2.5">
            <h3 id="archive-order-title" className="text-[0.95rem] font-semibold leading-6 text-slate-900">
              {copy.title}
            </h3>
          </div>
        </div>

        <div className={modalBodyClassName}>
          <p className="text-sm leading-6 text-slate-600">
            {copy.body}
          </p>
          {orderLabel ? (
            <p className="mt-3 break-words text-sm font-medium text-slate-800">{orderLabel}</p>
          ) : null}

          <div className="mb-4 mt-5">
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="archive-reason">
              Reason <span className="text-rose-500">*</span>
            </label>
            <textarea
              id="archive-reason"
              className="w-full rounded-[0.8rem] border border-slate-200 bg-white px-3 py-2.5 text-slate-900 outline-none transition focus:border-slate-300 focus:ring-2 focus:ring-slate-200 disabled:bg-slate-50"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setReasonError('');
              }}
              rows={2}
              disabled={loading}
              placeholder={copy.placeholder}
            />
            {reasonError ? <div className="mt-1 text-xs text-rose-600">{reasonError}</div> : null}
          </div>

          <div className="flex items-start rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5">
            <div className="flex h-5 items-center">
              <input
                id="archive-order-confirmation"
                name="archive-order-confirmation"
                type="checkbox"
                checked={confirmed}
                disabled={loading}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-slate-700 focus:ring-slate-700"
              />
            </div>
            <div className="ml-3 min-w-0 text-sm">
              <label htmlFor="archive-order-confirmation" className="text-slate-700">
                {copy.checkbox}
              </label>
            </div>
          </div>
        </div>

        <div className={modalFooterClassName}>
          <button
            type="button"
            className={`${modalPrimaryButtonClassName} bg-slate-900 hover:bg-slate-800 focus:ring-slate-400`}
            disabled={loading || !confirmed || !reason.trim()}
            onClick={handleConfirm}
          >
            {loading ? 'Archiving...' : 'Confirm'}
          </button>
          <button
            type="button"
            className={`${modalSecondaryButtonClassName} hover:border-slate-300 hover:bg-slate-100 focus:ring-slate-300`}
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ArchiveConfirmModal;
